/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Hotel, Client, CompanyProfile, ParkingSpot, AccommodationType, Accommodation, StockCategory, StockItem, Minibar, Reservation, SystemState, UserRole } from '../types';

const STORAGE_KEY = "hotel_pousada_management_state";

const DEFAULT_PROFILE: CompanyProfile = {
  name: "Pousada Recanto do Sol",
  corporateName: "F. M. Dos Santos Hospedagens LTDA",
  document: "28.190.283/0001-92",
  phone: "(21) 2634-1922",
  email: "contato@pousadarecantodosol.com.br",
  address: "Rua das Conchas, 145 - Centro, Búzios - RJ, CEP: 28950-000",
  pixKey: "contato@pousadarecantodosol.com.br",
};

export const DEFAULT_HOTELS: Hotel[] = [
  {
    id: "hotel_1",
    name: "Pousada Recanto do Sol",
    corporateName: "F. M. Dos Santos Hospedagens LTDA",
    document: "28.190.283/0001-92",
    phone: "(21) 2634-1922",
    email: "contato@pousadarecantodosol.com.br",
    address: "Rua das Conchas, 145 - Centro, Búzios - RJ, CEP: 28950-000",
    pixKey: "contato@pousadarecantodosol.com.br",
    active: true,
    createdAt: "2026-05-01",
    adminUser: "admin1",
    adminPass: "admin",
    recepUser: "recep1",
    recepPass: "recep"
  },
  {
    id: "hotel_2",
    name: "Hotel Mar e Brisa",
    corporateName: "Hospedagens Mar e Brisa LTDA",
    document: "19.281.938/0001-44",
    phone: "(21) 3789-4455",
    email: "mar.brisa@hoteisrecanto.com.br",
    address: "Av. Beira Mar, 890 - Praia de Geribá, Búzios - RJ, CEP: 28950-000",
    pixKey: "financeiro@marebrisa.com.br",
    active: true,
    createdAt: "2026-05-10",
    adminUser: "admin2",
    adminPass: "admin",
    recepUser: "recep2",
    recepPass: "recep"
  },
  {
    id: "hotel_3",
    name: "Pousada Eco Valle (Inativa)",
    corporateName: "Eco Valle Empreendimentos Hoteleiros",
    document: "45.123.890/0001-01",
    phone: "(24) 2234-9090",
    email: "contato@pousadaecovalle.com.br",
    address: "Estrada Imperial, Km 15 - Vale Alpino, Petrópolis - RJ, CEP: 25700-000",
    pixKey: "pix@pousadaecovalle.com.br",
    active: false,
    createdAt: "2026-05-12",
    adminUser: "admin3",
    adminPass: "admin",
    recepUser: "recep3",
    recepPass: "recep"
  }
];

const DEFAULT_CLIENTS: Client[] = [
  {
    id: "cli_1",
    name: "Roberto Silveira",
    birthDate: "1984-12-15",
    document: "123.456.789-10",
    email: "roberto.silveira@outlook.com",
    phone: "(21) 98122-3456",
    state: "Rio de Janeiro",
    city: "Niterói",
    active: true,
    createdAt: "2026-05-01",
  },
  {
    id: "cli_2",
    name: "Mariana Alencar",
    birthDate: "1992-06-23",
    document: "987.654.321-00",
    email: "mariana.alencar@gmail.com",
    phone: "(31) 99888-7766",
    state: "Minas Gerais",
    city: "Belo Horizonte",
    active: true,
    createdAt: "2026-05-15",
  },
  {
    id: "cli_3",
    name: "Lucas Fernandes Vasconcelos",
    birthDate: "1978-02-12",
    document: "456.789.123-45",
    email: "lucas.fv@uol.com.br",
    phone: "(11) 97111-2233",
    state: "São Paulo",
    city: "São Paulo",
    active: false,
    createdAt: "2026-04-20",
  },
];

const DEFAULT_PARKING_SPOTS: ParkingSpot[] = [
  { id: "pk_1", number: "101", active: true, status: 'Ocupado' },
  { id: "pk_2", number: "102", active: true, status: 'Ocupado' },
  { id: "pk_3", number: "103", active: true, status: 'Disponível' },
  { id: "pk_4", number: "104", active: true, status: 'Disponível' },
  { id: "pk_5", number: "201", active: true, status: 'Disponível' },
  { id: "pk_6", number: "202", active: false, status: 'Disponível' },
];

const DEFAULT_ACCOMMODATION_TYPES: AccommodationType[] = [
  { id: "tp_1", name: "Suíte Master Frente Mar", active: true },
  { id: "tp_2", name: "Quarto Standard com Varanda", active: true },
  { id: "tp_3", name: "Chalé Privativo Familiar", active: true },
];

const DEFAULT_ACCOMMODATIONS: Accommodation[] = [
  {
    id: "ac_101",
    name: "Suíte Master Frente Mar - Quarto 101",
    number: "101",
    typeId: "tp_1",
    rate: 350.00,
    capacity: 2,
    description: "Cama king size, ar-condicionado, varanda com rede, frigobar retrô e banheira de hidromassagem.",
    parkingSpots: ["101"],
    active: true,
  },
  {
    id: "ac_102",
    name: "Quarto Standard Jardim - Quarto 102",
    number: "102",
    typeId: "tp_2",
    rate: 220.00,
    capacity: 3,
    description: "Cama de casal + solteiro, ar-condicionado silencioso e TV a cabo com canais HD.",
    parkingSpots: ["102"],
    active: true,
  },
  {
    id: "ac_201",
    name: "Chalé Família Bosque - Quarto 201",
    number: "201",
    typeId: "tp_3",
    rate: 550.00,
    capacity: 5,
    description: "Chalé amplo de dois andares com cozinha gourmet, lareira ecológica e varanda panorâmica.",
    parkingSpots: ["103", "104"],
    active: true,
  },
  {
    id: "ac_202",
    name: "Quarto Standard Lateral - Quarto 202",
    number: "202",
    typeId: "tp_2",
    rate: 180.00,
    capacity: 2,
    description: "Cama de casal básica, ar-condicionado e frigobar simples.",
    parkingSpots: [],
    active: false,
  },
];

const DEFAULT_STOCK_CATEGORIES: StockCategory[] = [
  { id: "sc_1", name: "Bebidas", active: true },
  { id: "sc_2", name: "Salgados & Doces", active: true },
  { id: "sc_3", name: "Higiene Pessoal", active: true },
];

const DEFAULT_STOCK_ITEMS: StockItem[] = [
  { id: "si_1", categoryId: "sc_1", name: "Água Mineral 350ml", quantity: 45, minLimit: 5, price: 4.50, active: true },
  { id: "si_2", categoryId: "sc_1", name: "Cerveja Heineken Lata 350ml", quantity: 82, minLimit: 10, price: 9.00, active: true },
  { id: "si_3", categoryId: "sc_1", name: "Refrigerante Coca-Cola Lata", quantity: 68, minLimit: 8, price: 6.00, active: true },
  { id: "si_4", categoryId: "sc_2", name: "Batata Chips Pringles Peq", quantity: 24, minLimit: 4, price: 12.00, active: true },
  { id: "si_5", categoryId: "sc_2", name: "Chocolate Barra Milka", quantity: 18, minLimit: 3, price: 15.00, active: true },
  { id: "si_6", categoryId: "sc_3", name: "Kit Barbear Premium", quantity: 12, minLimit: 2, price: 18.00, active: true },
];

const DEFAULT_MINIBARS: Minibar[] = [
  {
    id: "mb_1",
    model: "Brastemp Retrô Vermelha",
    tag: "FR-RET-01",
    accommodationId: "ac_101",
    items: [
      { itemId: "si_1", quantity: 4 },
      { itemId: "si_2", quantity: 6 },
      { itemId: "si_3", quantity: 4 },
    ],
    active: true,
  },
  {
    id: "mb_2",
    model: "Consul Compact 120 Litros",
    tag: "FR-CON-02",
    accommodationId: "ac_102",
    items: [
      { itemId: "si_1", quantity: 2 },
      { itemId: "si_3", quantity: 3 },
    ],
    active: true,
  },
];

const DEFAULT_RESERVATIONS: Reservation[] = [
  {
    id: "res_1",
    code: "RES-101",
    accommodationId: "ac_101",
    clientId: "cli_1",
    checkInDate: "2026-05-10",
    checkOutDate: "2026-05-15",
    status: "Finalizado",
    guestsCount: 2,
    dailyRate: 350.00,
    notes: "Comemorando aniversário de casamento. Solicitaram mimos extras se possível.",
    checkInDetails: {
      realizedBy: "Administrador",
      date: "2026-05-10",
      time: "14:15",
    },
    checkOutDateActual: "2026-05-15",
    checkOutTimeActual: "11:30",
    consumptions: [
      { id: "c_1", type: "frigobar", itemId: "si_2", description: "Cerveja Heineken Lata 350ml", quantity: 4, price: 9.00, total: 36.00, date: "2026-05-11 18:00:00" },
      { id: "c_2", type: "adicional", description: "Taxa de Lavanderia Comercial", quantity: 1, price: 25.00, total: 25.00, date: "2026-05-12 10:20:00" },
    ],
    additionalCosts: [
      { id: "acost_1", description: "Taxa Especial de Rebanho/Pet", amount: 50.00 },
    ],
    totalStayAmount: 1750.00, // 5 * 350
    totalConsumptionAmount: 61.00, // 36 + 25
    totalAdditionalAmount: 50.00,
    finalTotal: 1861.00,
    createdAt: "2026-05-01",
  },
  {
    id: "res_2",
    code: "RES-102",
    accommodationId: "ac_102",
    clientId: "cli_2",
    checkInDate: "2026-05-25",
    checkOutDate: "2026-06-02",
    status: "Iniciado",
    guestsCount: 2,
    dailyRate: 220.00,
    notes: "Hóspede alérgica a poeira, exige limpeza e ventilação caprichada.",
    checkInDetails: {
      realizedBy: "Recepcionista",
      date: "2026-05-25",
      time: "13:40",
    },
    consumptions: [
      { id: "c_3", type: "frigobar", itemId: "si_1", description: "Água Mineral 350ml", quantity: 2, price: 4.50, total: 9.00, date: "2026-05-26 21:10:00" },
      { id: "c_4", type: "adicional", description: "Almoço Executivo Peixe", quantity: 1, price: 45.00, total: 45.00, date: "2026-05-28 13:00:00" },
    ],
    additionalCosts: [],
    totalStayAmount: 0,
    totalConsumptionAmount: 54.00,
    totalAdditionalAmount: 0,
    finalTotal: 54.00,
    createdAt: "2025-05-15",
  },
  {
    id: "res_3",
    code: "RES-103",
    accommodationId: "ac_201",
    clientId: "cli_1",
    checkInDate: "2026-06-05",
    checkOutDate: "2026-06-12",
    status: "Pendente",
    guestsCount: 4,
    dailyRate: 550.00,
    notes: "Hospedagem em grupo familiar amplo. Virão em dois carros.",
    consumptions: [],
    additionalCosts: [],
    totalStayAmount: 0,
    totalConsumptionAmount: 0,
    totalAdditionalAmount: 0,
    finalTotal: 0,
    createdAt: "2026-06-01",
  },
];

export function getInitialState(): SystemState {
  const hotel1Spots = DEFAULT_PARKING_SPOTS.map(s => ({ ...s, hotelId: "hotel_1" }));
  const hotel1Accs = DEFAULT_ACCOMMODATIONS.map(a => ({ ...a, hotelId: "hotel_1" }));
  const hotel1Stock = DEFAULT_STOCK_ITEMS.map(i => ({ ...i, hotelId: "hotel_1" }));
  const hotel1Minibars = DEFAULT_MINIBARS.map(m => ({ ...m, hotelId: "hotel_1" }));
  const hotel1Reservas = DEFAULT_RESERVATIONS.map(r => ({ ...r, hotelId: "hotel_1" }));

  // Pre-populated data for Hotel 2 (Hotel Mar e Brisa) to demonstrate separation immediately
  const hotel2Spots: ParkingSpot[] = [
    { id: "pk_h2_1", number: "301", active: true, status: 'Ocupado', hotelId: "hotel_2" },
    { id: "pk_h2_2", number: "302", active: true, status: 'Disponível', hotelId: "hotel_2" },
    { id: "pk_h2_3", number: "401", active: true, status: 'Disponível', hotelId: "hotel_2" }
  ];
  const hotel2Accs: Accommodation[] = [
    {
      id: "ac_h2_1",
      name: "Apartamento Luxo - Quarto 301",
      number: "301",
      typeId: "tp_1",
      rate: 420.00,
      capacity: 3,
      description: "Quarto com ar-condicionado Split, cama King Size, TV de 55 e frigobar com vinhos.",
      parkingSpots: ["301"],
      active: true,
      hotelId: "hotel_2"
    },
    {
      id: "ac_h2_2",
      name: "Apartamento Conjugado - Quarto 302",
      number: "302",
      typeId: "tp_2",
      rate: 310.00,
      capacity: 4,
      description: "Dois quartos integrados com camas confortáveis de casal e solteiro. Ideal para famílias.",
      parkingSpots: ["302"],
      active: true,
      hotelId: "hotel_2"
    }
  ];
  const hotel2Stock: StockItem[] = [
    { id: "si_h2_1", categoryId: "sc_1", name: "Água com Gás 310ml", quantity: 30, minLimit: 5, price: 5.00, active: true, hotelId: "hotel_2" },
    { id: "si_h2_2", categoryId: "sc_1", name: "Cerveja Corona Long Neck", quantity: 50, minLimit: 10, price: 12.00, active: true, hotelId: "hotel_2" },
    { id: "si_h2_3", categoryId: "sc_1", name: "Refrigerante Fanta Guaraná", quantity: 40, minLimit: 8, price: 6.50, active: true, hotelId: "hotel_2" }
  ];
  const hotel2Minibars: Minibar[] = [
    {
      id: "mb_h2_1",
      model: "Electrolux Glass Door",
      tag: "FR-EL-99",
      accommodationId: "ac_h2_1",
      items: [
        { itemId: "si_h2_1", quantity: 3 },
        { itemId: "si_h2_2", quantity: 4 }
      ],
      active: true,
      hotelId: "hotel_2"
    }
  ];
  const hotel2Reservas: Reservation[] = [
    {
      id: "res_h2_1",
      code: "RES-H2-01",
      accommodationId: "ac_h2_1",
      clientId: "cli_2",
      checkInDate: "2026-05-28",
      checkOutDate: "2026-06-03",
      status: "Iniciado",
      guestsCount: 2,
      dailyRate: 420.00,
      notes: "Hospedagem no Hotel Mar e Brisa. Solicitou check-out tardio.",
      checkInDetails: {
        realizedBy: "Recepcionista Mar e Brisa",
        date: "2026-05-28",
        time: "15:00",
      },
      consumptions: [
        { id: "c_h2_1", type: "frigobar", itemId: "si_h2_2", description: "Cerveja Corona Long Neck", quantity: 2, price: 12.00, total: 24.00, date: "2026-05-29 19:30:00" }
      ],
      additionalCosts: [],
      totalStayAmount: 0,
      totalConsumptionAmount: 24.00,
      totalAdditionalAmount: 0,
      finalTotal: 24.00,
      createdAt: "2026-05-20",
      hotelId: "hotel_2"
    }
  ];

  const setupInitialSpots = [...hotel1Spots, ...hotel2Spots];
  const setupInitialAccs = [...hotel1Accs, ...hotel2Accs];
  const setupInitialStock = [...hotel1Stock, ...hotel2Stock];
  const setupInitialMinibars = [...hotel1Minibars, ...hotel2Minibars];
  const setupInitialReservas = [...hotel1Reservas, ...hotel2Reservas];

  if (typeof window === "undefined") {
    return {
      profile: DEFAULT_PROFILE,
      hotels: DEFAULT_HOTELS,
      selectedHotelId: "hotel_1",
      clients: DEFAULT_CLIENTS,
      parkingSpots: setupInitialSpots,
      accommodationTypes: DEFAULT_ACCOMMODATION_TYPES,
      accommodations: setupInitialAccs,
      stockCategories: DEFAULT_STOCK_CATEGORIES,
      stockItems: setupInitialStock,
      minibars: setupInitialMinibars,
      reservations: setupInitialReservas,
      activeRole: 'administrador',
      isLoggedIn: false, // Default to false for manual login simulation
    };
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return {
          profile: parsed.profile || DEFAULT_PROFILE,
          hotels: parsed.hotels || DEFAULT_HOTELS,
          selectedHotelId: parsed.selectedHotelId || "hotel_1",
          clients: parsed.clients || DEFAULT_CLIENTS,
          parkingSpots: (parsed.parkingSpots || setupInitialSpots),
          accommodationTypes: parsed.accommodationTypes || DEFAULT_ACCOMMODATION_TYPES,
          accommodations: (parsed.accommodations || setupInitialAccs),
          stockCategories: parsed.stockCategories || DEFAULT_STOCK_CATEGORIES,
          stockItems: (parsed.stockItems || setupInitialStock),
          minibars: (parsed.minibars || setupInitialMinibars),
          reservations: (parsed.reservations || setupInitialReservas),
          activeRole: parsed.activeRole || 'administrador',
          isLoggedIn: parsed.isLoggedIn !== undefined ? parsed.isLoggedIn : false,
        };
      }
    }
  } catch (err) {
    console.error("Failed to load state from localStorage:", err);
  }

  const defaultState: SystemState = {
    profile: DEFAULT_PROFILE,
    hotels: DEFAULT_HOTELS,
    selectedHotelId: "hotel_1",
    clients: DEFAULT_CLIENTS,
    parkingSpots: setupInitialSpots,
    accommodationTypes: DEFAULT_ACCOMMODATION_TYPES,
    accommodations: setupInitialAccs,
    stockCategories: DEFAULT_STOCK_CATEGORIES,
    stockItems: setupInitialStock,
    minibars: setupInitialMinibars,
    reservations: setupInitialReservas,
    activeRole: 'administrador',
    isLoggedIn: false,
  };

  saveState(defaultState);
  return defaultState;
}

export function saveState(state: SystemState): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error("Failed to save state to localStorage:", err);
  }
}

export function formatCurrencyValue(val: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
}

export function calculateNights(startStr: string, endStr: string): number {
  if (!startStr || !endStr) return 1;
  const s = new Date(startStr);
  const e = new Date(endStr);
  const diff = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 1;
}

export function formatLocalDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
}

export function normalizeString(str: string | null | undefined): string {
  if (!str) return '';
  return String(str)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function asArray<T = any>(value: any, fallback: T[] = []): T[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export function asObject<T = any>(value: any, fallback: T | undefined = undefined): T | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as T;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed as T;
    } catch { /* ignore */ }
  }
  return fallback;
}

export function pluralizar(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
